import { ProjectGovernanceState, ProjectStageStatus } from './project-governance.js';
import type { ProjectStatus } from './project.js';
import { ToleranceArea, ToleranceStatus, type ToleranceEvaluationDto } from './project-controls.js';
import type { MonitoringAlertDto, MonitoringHealthDto } from './project-monitoring.js';

/** The 5 PRINCE2 phases of the portfolio view, in process order. */
export const PortfolioPhase = {
  StartingUp: 'startingUp',
  Initiating: 'initiating',
  ControllingStages: 'controllingStages',
  Handover: 'handover',
  Closing: 'closing',
} as const;
export type PortfolioPhase = (typeof PortfolioPhase)[keyof typeof PortfolioPhase];
export const PortfolioPhaseValues = Object.values(PortfolioPhase);

export const PortfolioRag = {
  Red: 'RED',
  Amber: 'AMBER',
  Green: 'GREEN',
  NotSet: 'NOT_SET',
} as const;
export type PortfolioRag = (typeof PortfolioRag)[keyof typeof PortfolioRag];
export const PortfolioRagValues = Object.values(PortfolioRag);

export const PortfolioDataStatus = {
  Current: 'CURRENT',
  Stale: 'STALE',
  Missing: 'MISSING',
} as const;
export type PortfolioDataStatus = (typeof PortfolioDataStatus)[keyof typeof PortfolioDataStatus];
export const PortfolioDataStatusValues = Object.values(PortfolioDataStatus);

export const ManagementProductType = {
  Brief: 'BRIEF',
  Pid: 'PID',
  EndStage: 'END_STAGE',
  EndProject: 'END_PROJECT',
  PrematureClosure: 'PREMATURE_CLOSURE',
  Highlight: 'HIGHLIGHT',
} as const;
export type ManagementProductType = (typeof ManagementProductType)[keyof typeof ManagementProductType];
export const ManagementProductTypeValues = Object.values(ManagementProductType);

export const ChangeAdoptionKind = {
  AffectedGroup: 'AFFECTED_GROUP',
  Training: 'TRAINING',
  Communication: 'COMMUNICATION',
} as const;
export type ChangeAdoptionKind = (typeof ChangeAdoptionKind)[keyof typeof ChangeAdoptionKind];
export const ChangeAdoptionKindValues = Object.values(ChangeAdoptionKind);

export const ProjectLogSource = {
  Audit: 'AUDIT',
  Monitoring: 'MONITORING',
} as const;
export type ProjectLogSource = (typeof ProjectLogSource)[keyof typeof ProjectLogSource];
export const ProjectLogSourceValues = Object.values(ProjectLogSource);

/** Governance states in which the Executive must record a decision. */
export const PORTFOLIO_AWAITING_DECISION_STATES: ProjectGovernanceState[] = [
  ProjectGovernanceState.AwaitingInitiationAuthorization,
  ProjectGovernanceState.AwaitingProjectAuthorization,
  ProjectGovernanceState.AwaitingNextStageAuthorization,
  ProjectGovernanceState.ReadyForClosure,
];

export interface PortfolioRowDto {
  id: string;
  title: string;
  code: string | null;
  currency: string;
  phase: PortfolioPhase | null;
  governanceState: ProjectGovernanceState | null;
  commercialStatus: ProjectStatus;
  progress: number;
  nextMilestoneDate: string | null;
  active: boolean;
  exception: boolean;
  paused: boolean;
  awaitingMyDecision: boolean;
  lastCheckAt: string | null;
  rag: PortfolioRag;
  dataStatus: PortfolioDataStatus;
  baselineBudget: number | null;
  actual: number | null;
  plannedToDate: number | null;
  eac: number | null;
}

export interface PortfolioAlertItemDto extends MonitoringAlertDto {
  projectTitle: string;
}

export interface PortfolioDto {
  asOf: string;
  rows: PortfolioRowDto[];
  alerts: PortfolioAlertItemDto[];
  unmatchedCostCount: number;
  health: MonitoringHealthDto;
  currency: string | null;
  totalBudget: number | null;
  spendVsPlanPercent: number | null;
  redCount: number;
}

export interface ProjectManagementProductDto {
  id: string;
  type: ManagementProductType;
  version: number;
  requestId: string | null;
  followOnActions: string[] | null;
  snapshot: Record<string, unknown>;
  createdAt: string;
}

export interface ProjectLogItemDto {
  id: string;
  at: string;
  source: ProjectLogSource;
  eventType: string;
  detail: string | null;
}

export interface ProjectChangeAdoptionItemDto {
  id: string;
  kind: ChangeAdoptionKind;
  title: string;
  detail: string | null;
  ownerUserId: string | null;
  target: string | null;
  dueDate: string | null;
  done: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectChangeAdoptionItemDto {
  kind: ChangeAdoptionKind;
  title: string;
  detail?: string | null;
  ownerUserId?: string | null;
  target?: string | null;
  dueDate?: string | null;
}

export interface UpdateProjectChangeAdoptionItemDto {
  expectedVersion: number;
  title?: string;
  detail?: string | null;
  ownerUserId?: string | null;
  target?: string | null;
  dueDate?: string | null;
  done?: boolean;
}

export function phaseFor(governance: {
  state: ProjectGovernanceState;
  stages: { order: number; status: string }[];
}): PortfolioPhase {
  switch (governance.state) {
    case ProjectGovernanceState.Preparation:
    case ProjectGovernanceState.AwaitingInitiationAuthorization:
      return PortfolioPhase.StartingUp;
    case ProjectGovernanceState.Initiation:
    case ProjectGovernanceState.AwaitingProjectAuthorization:
      return PortfolioPhase.Initiating;
    case ProjectGovernanceState.StageActive:
      return isFinalStageActive(governance.stages) ? PortfolioPhase.Handover : PortfolioPhase.ControllingStages;
    case ProjectGovernanceState.AwaitingNextStageAuthorization:
      return PortfolioPhase.ControllingStages;
    case ProjectGovernanceState.ReadyForClosure:
    case ProjectGovernanceState.Closed:
      return PortfolioPhase.Closing;
  }
}

function isFinalStageActive(stages: { order: number; status: string }[]): boolean {
  const ordered = stages.slice().sort((a, b) => a.order - b.order);
  const active = ordered.filter((s) => s.status === ProjectStageStatus.Authorized).pop();
  if (!active) return false;
  return !ordered.some((s) => s.order > active.order && s.status === ProjectStageStatus.Planned);
}

/** Red on exception or forecast exception. Amber on warning. Not set when cost or time has no limit. */
export function ragFor(input: {
  exception: boolean;
  evaluation: Pick<ToleranceEvaluationDto, 'area' | 'status'>[] | null;
}): PortfolioRag {
  if (input.exception) return PortfolioRag.Red;
  const evaluation = input.evaluation ?? [];
  if (evaluation.some((e) => e.status === ToleranceStatus.ForecastException)) return PortfolioRag.Red;
  if (evaluation.some((e) => e.status === ToleranceStatus.Warning)) return PortfolioRag.Amber;
  const cost = evaluation.find((e) => e.area === ToleranceArea.Cost);
  const time = evaluation.find((e) => e.area === ToleranceArea.Time);
  if (
    !cost ||
    !time ||
    cost.status === ToleranceStatus.NotSet ||
    time.status === ToleranceStatus.NotSet ||
    cost.status === ToleranceStatus.CannotEvaluate ||
    time.status === ToleranceStatus.CannotEvaluate
  ) {
    return PortfolioRag.NotSet;
  }
  if (cost.status === ToleranceStatus.Within && time.status === ToleranceStatus.Within) {
    return PortfolioRag.Green;
  }
  return PortfolioRag.NotSet;
}

/** Monday-to-Friday days after `from`, up to and including `to`. */
export function portfolioWorkingDaysBetween(from: Date, to: Date): number {
  let count = 0;
  const day = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
  const end = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate());
  while (day.getTime() < end) {
    day.setUTCDate(day.getUTCDate() + 1);
    const weekday = day.getUTCDay();
    if (weekday !== 0 && weekday !== 6) count += 1;
  }
  return count;
}

export function dataStatusFor(
  lastCheckAt: string | null,
  staleWorkingDays: number,
  now: Date,
): PortfolioDataStatus {
  if (!lastCheckAt) return PortfolioDataStatus.Missing;
  const days = portfolioWorkingDaysBetween(new Date(lastCheckAt), now);
  if (days > staleWorkingDays) return PortfolioDataStatus.Stale;
  return PortfolioDataStatus.Current;
}

export function isAwaitingPortfolioDecision(input: {
  executiveUserId: string | null;
  userId: string;
  state: ProjectGovernanceState;
  exception: boolean;
}): boolean {
  if (input.executiveUserId !== input.userId) return false;
  return PORTFOLIO_AWAITING_DECISION_STATES.includes(input.state) || input.exception;
}

export function portfolioTotals(rows: Pick<PortfolioRowDto, 'currency' | 'baselineBudget' | 'actual' | 'plannedToDate' | 'rag'>[]): {
  currency: string | null;
  totalBudget: number | null;
  spendVsPlanPercent: number | null;
  redCount: number;
} {
  const redCount = rows.filter((r) => r.rag === PortfolioRag.Red).length;
  const currencies = [...new Set(rows.map((r) => r.currency))];
  if (currencies.length !== 1) {
    return { currency: null, totalBudget: null, spendVsPlanPercent: null, redCount };
  }
  const totalBudget = rows.reduce((sum, r) => sum + (r.baselineBudget ?? 0), 0);
  const actual = rows.reduce((sum, r) => sum + (r.actual ?? 0), 0);
  const plan = rows.reduce((sum, r) => sum + (r.plannedToDate ?? r.baselineBudget ?? 0), 0);
  return {
    currency: currencies[0],
    totalBudget,
    spendVsPlanPercent: plan > 0 ? Math.round((100 * actual) / plan) : null,
    redCount,
  };
}
